import React, { useState, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { loadLanguage } from "../../i18n/i18n";
import { MenuItem, Select, FormControl, InputLabel } from "@mui/material";

const LanguageSelector = ({ languages, value, onChange, label, variant = "light" }) => {
  const { t, i18n } = useTranslation();
  const autoId = useId();
  const isDark = variant === "dark";
  const accessibleLabel = label || t("common.selectLanguage", "Select Language");
  const labelId = `language-selector-label-${autoId}`;
  const selectId = `language-selector-${autoId}`;
  const textColor = isDark ? "#ffffff" : "var(--primary-color, #304577)";
  const backgroundColor = isDark ? "var(--primary-color, #304577)" : "#ffffff";
  const borderColor = isDark
    ? "var(--light-blue-color, #d7eff5)"
    : "var(--secondary-color, #08bbcf)";
  const [selectedLang, setSelectedLang] = useState(
    value || localStorage.getItem("userLanguage") || "en"
  );

  useEffect(() => {
    if (value) {
      setSelectedLang(value);
    }
  }, [value]);

  const handleChange = async (e) => {
    const lang = e.target.value;
    setSelectedLang(lang);
    localStorage.setItem("userLanguage", lang);
    await loadLanguage(lang);
    i18n.changeLanguage(lang);
    if (onChange) {
      onChange(lang);
    }
  };

  const displayLanguages =
    languages && languages.length > 0
      ? languages
      : [{ code: "en", name: "English" }];

  return (
    <FormControl
      fullWidth
      variant="outlined"
      size="small"
      className={`language-selector language-selector-${variant}`}
      sx={{
        backgroundColor,
        "& .MuiInputLabel-root": {
          color: textColor,
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: textColor,
        },
        "& .MuiOutlinedInput-root": {
          backgroundColor,
          borderRadius: "40px",
          color: textColor,
          "& fieldset": {
            borderColor,
          },
          "&:hover fieldset": {
            borderColor,
          },
          "&.Mui-focused fieldset": {
            borderColor,
          },
        },
        "& .MuiOutlinedInput-input": {
          color: `${textColor} !important`,
          WebkitTextFillColor: textColor,
        },
        "& .MuiSelect-select": {
          // Explicit, non-transparent background on the innermost slot.
          // Fixes the "background color could not be determined" scan
          // warning caused by this node inheriting from an ancestor
          // instead of carrying its own resolvable background.
          backgroundColor,
          color: `${textColor} !important`,
          WebkitTextFillColor: textColor,
        },
      }}
    >
      <InputLabel id={labelId} htmlFor={selectId}>{accessibleLabel}</InputLabel>
      <Select
        id={selectId}
        labelId={labelId}
        aria-label={accessibleLabel}
        title={accessibleLabel}
        value={selectedLang}
        onChange={handleChange}
        label={accessibleLabel}
        inputProps={{
          "aria-label": accessibleLabel,
          title: accessibleLabel,
        }}
        MenuProps={{
          // Kill the Grow/Fade open transition entirely. This removes the
          // intermediate opacity-animating frame where menu text/background
          // blend with whatever sits behind the menu (previously caused the
          // 14 confirmed contrast failures picked up by automated scans).
          transitionDuration: 0,
          PaperProps: {
            sx: {
              backgroundColor: "#ffffff",
              color: "var(--primary-color, #304577)",
            },
          },
        }}
      >
        {displayLanguages.map((lang) => (
          <MenuItem
            key={lang.code}
            value={lang.code}
            // Disables the MuiTouchRipple overlay span, which was sitting on
            // top of MenuItem at click/scan time and caused "background
            // color could not be determined because it is overlapped"
            // false positives on the option list items.
            disableRipple
            sx={{
              backgroundColor: "#ffffff",
              color: "var(--primary-color, #304577)",
              "&:hover, &.Mui-focusVisible": {
                backgroundColor: "var(--light-blue-color, #d7eff5)",
              },
              "&.Mui-selected, &.Mui-selected:hover": {
                backgroundColor: "var(--light-blue-color, #d7eff5)",
                color: "var(--primary-color, #304577)",
              },
            }}
          >
            {lang.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;